import { AlertTriangle, Clock, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import en from "../../locales/en";
import cs from "../../locales/cs";

/**
 * ExpiringSoonBanner - Displays warnings for expiring and expired authorizations
 * 
 * Shows:
 * - Amber banner for expiring soon (within 30 days)
 * - Red banner for already expired
 */
export const ExpiringSoonBanner = ({
  expiringSoonWarnings = [],
  expiredWarnings = [],
  onRenewClick,
  language = "cs",
}) => {
  const navigate = useNavigate();
  const tExpiring = language === "cs" ? cs.checkmate.expiringSoon : en.checkmate.expiringSoon;
  const tExpired = language === "cs" ? cs.checkmate.expired : en.checkmate.expired;
  
  // If both lists empty, render nothing
  if (expiringSoonWarnings.length === 0 && expiredWarnings.length === 0) {
    return null;
  }
  
  const handleRenew = (authId) => {
    if (onRenewClick) {
      onRenewClick(authId);
    } else {
      navigate(`/authorizations?highlight=${authId}`);
    }
  };
  
  // Format message with placeholders
  const formatMessage = (template, warning) => {
    return template
      .replace("{type}", warning.authorization_type || "Auth")
      .replace("{ref}", warning.reference_number || "N/A")
      .replace("{days}", warning.days_until_expiry?.toString() || "?")
      .replace("{date}", warning.valid_until ? new Date(warning.valid_until).toLocaleDateString(language === "cs" ? "cs-CZ" : "en-US") : "N/A");
  };

  return (
    <div className="space-y-2 mb-4" data-testid="expiring-soon-banner">
      {/* Expired authorizations - Red banner */}
      {expiredWarnings.length > 0 && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-200">
                {tExpired.bannerTitle}
              </h4>
              <div className="mt-1 space-y-1">
                {expiredWarnings.map((warning) => (
                  <div 
                    key={warning.authorization_id} 
                    className="flex items-center justify-between text-xs text-red-700 dark:text-red-300"
                  >
                    <span>{formatMessage(tExpired.item, warning)}</span>
                    <button
                      onClick={() => handleRenew(warning.authorization_id)}
                      className="flex items-center gap-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
                    >
                      {tExpiring.renewLink}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Expiring soon - Amber banner */}
      {expiringSoonWarnings.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <div className="flex items-start gap-2">
            <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                {tExpiring.bannerTitle}
              </h4>
              <div className="mt-1 space-y-1">
                {expiringSoonWarnings.map((warning) => (
                  <div 
                    key={warning.authorization_id} 
                    className="flex items-center justify-between text-xs text-amber-700 dark:text-amber-300"
                  >
                    <span>{formatMessage(tExpiring.item, warning)}</span>
                    <button
                      onClick={() => handleRenew(warning.authorization_id)}
                      className="flex items-center gap-1 text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 underline"
                    >
                      {tExpiring.renewLink}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpiringSoonBanner;
